import React, { useState } from 'react';
import { WordDefinition, Definition } from '../types/dictionary';

interface WordCardProps {
  data: WordDefinition;
  onTagClick?: (term: string) => void;
}

const DefinitionItem: React.FC<{ def: Definition }> = ({ def }) => (
  <div className={`mb-3 ${def.visualWeight === 'light' ? 'opacity-75' : ''}`}>
    <div className="flex items-baseline gap-2">
      <span className="text-xs font-bold uppercase tracking-wider text-slate-500">
        {def.partOfSpeech}
      </span>
      <span className="font-serif text-lg text-slate-900 dark:text-slate-100">
        {def.meaning}
      </span>
    </div>
    {def.context && (
      <div className="text-sm text-slate-500 italic ml-2">
        Context: {def.context}
      </div>
    )}
    {def.examRelevance && (
      <div className="text-xs text-indigo-600 dark:text-indigo-400 mt-1 ml-2 font-medium">
        ★ {def.examRelevance}
      </div>
    )}
  </div>
);

const EssayBlock: React.FC<{ argument?: string; template?: string }> = ({ argument, template }) => {
  if (!argument && !template) return null;
  return (
    <div className="mt-6 border-l-4 border-slate-400 bg-slate-50 dark:bg-slate-800/50 p-4 rounded-r-md">
      <h4 className="text-sm font-bold uppercase tracking-widest text-slate-500 mb-2">
        Essay Module
      </h4>
      {argument && (
        <div className="mb-3">
          <p className="text-sm font-serif text-slate-800 dark:text-slate-200 leading-relaxed">
            <span className="font-bold text-slate-400 mr-2">ARG:</span>
            {argument}
          </p>
        </div>
      )}
      {template && (
        <div className="bg-white dark:bg-slate-900 p-3 rounded border border-slate-200 dark:border-slate-700 font-mono text-sm text-slate-600 dark:text-slate-400">
          <span className="text-xs text-slate-400 block mb-1">TEMPLATE:</span>
          {template}
        </div>
      )}
    </div>
  );
};

const ExamplesSection: React.FC<{ examples: string[] }> = ({ examples }) => {
  if (!examples || examples.length === 0) return null;
  return (
    <div className="mb-6">
      <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3">
        Usage Examples
      </h3>
      <div className="space-y-2">
        {examples.map((example, i) => (
          <div key={i} className="pl-4 border-l-2 border-blue-200 dark:border-blue-800">
            <p className="text-sm italic text-slate-600 dark:text-slate-400 leading-relaxed">
              "{example}"
            </p>
          </div>
        ))}
      </div>
    </div>
  );
};

const EtymologySection: React.FC<{ etymology?: string }> = ({ etymology }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  if (!etymology) return null;

  return (
    <div className="mb-6">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="flex items-center gap-2 text-xs font-bold text-slate-400 uppercase tracking-wider hover:text-slate-600 dark:hover:text-slate-300 transition-colors"
      >
        <span className="transform transition-transform duration-200" style={{ transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)' }}>
          ▶
        </span>
        Etymology
      </button>
      {isExpanded && (
        <div className="mt-3 pl-4 border-l-2 border-green-200 dark:border-green-800">
          <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed">
            {etymology}
          </p>
        </div>
      )}
    </div>
  );
};

export const WordCard: React.FC<WordCardProps> = ({ data, onTagClick }) => {
  return (
    <div className="bg-white dark:bg-slate-800 shadow-xl rounded-lg overflow-hidden max-w-2xl w-full mx-auto my-4 border border-slate-200 dark:border-slate-700">
      {/* Header */}
      <div className="bg-slate-50 dark:bg-slate-900/50 px-6 py-4 border-b border-slate-100 dark:border-slate-700 flex justify-between items-start">
        <div>
          <h2 className="text-3xl font-serif font-bold text-slate-900 dark:text-white">
            {data.word}
          </h2>
          <div className="text-slate-500 font-mono text-sm mt-1">
            /{data.ipa}/
          </div>
        </div>
        <div className="text-right">
           <div className="text-sm font-bold text-slate-900 dark:text-slate-100">
             {data.academicTranslation}
           </div>
        </div>
      </div>

      {/* Agent Warning */}
      {data.warning && (
        <div className="bg-amber-50 dark:bg-amber-900/20 px-6 py-3 border-b border-amber-100 dark:border-amber-800/50 flex gap-3 items-start">
          <span className="text-amber-500 text-lg">⚠</span>
          <p className="text-sm text-amber-800 dark:text-amber-200 leading-tight pt-1">
            <span className="font-bold mr-1">Agent Warning:</span>
            {data.warning}
          </p>
        </div>
      )}

      {/* Content */}
      <div className="p-6">
        {/* Concept Summary */}
        <div className="mb-6">
          <p className="text-slate-700 dark:text-slate-300 leading-relaxed font-serif">
            {data.conceptSummary}
          </p>
        </div>

        {/* Definitions */}
        {data.definitions && data.definitions.length > 0 && (
          <div className="mb-6">
            <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3">
              Definitions
            </h3>
            {data.definitions.map((def, i) => (
              <DefinitionItem key={i} def={def} />
            ))}
          </div>
        )}

        {/* Usage Examples */}
        <ExamplesSection examples={data.examples} />

        {/* Etymology (collapsible) */}
        <EtymologySection etymology={data.etymology} />

        {/* Essay Block */}
        <EssayBlock argument={data.essayArgument} template={data.essayTemplate} />

        {/* Related Terms */}
        {data.relatedTerms && data.relatedTerms.length > 0 && (
          <div className="mt-6 pt-4 border-t border-slate-100 dark:border-slate-700">
             <div className="flex flex-wrap gap-2">
               {data.relatedTerms.map((term, i) => (
                 <button
                   key={i}
                   onClick={() => onTagClick?.(term.term)}
                   title={term.note || undefined}
                   className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-800 dark:bg-slate-700 dark:text-slate-300 hover:bg-blue-100 dark:hover:bg-blue-900 hover:text-blue-800 dark:hover:text-blue-200 cursor-pointer transition-colors"
                 >
                   {term.relationship === 'antonym' && '↔ '}
                   {term.relationship === 'thinker' && '👤 '}
                   {term.term}
                 </button>
               ))}
             </div>
          </div>
        )}
      </div>
    </div>
  );
};
